import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import LegalHeader from '../components/LegalHeader';

const EULA = () => {
    const { t } = useLanguage();

    const lastUpdatedDate = '2 de junio de 2026';
    const s = t('eula.sections');

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Navigation Header */}
            <LegalHeader />

            {/* Header */}
            <div className="bg-white shadow-sm">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
                    <div className="flex justify-center items-center space-x-4 mb-4">
                        <img
                            src="/farm-logo.png"
                            alt="FARM Logo"
                            className="w-12 h-12 rounded-xl"
                        />
                        <h1 className="text-3xl font-bold text-gray-900">{t('eula.title')}</h1>
                    </div>
                    <p className="text-gray-600">{t('eula.subtitle')}</p>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">

                    {/* Introduction */}
                    <section className="space-y-4 text-gray-700">
                        <p>{t('eula.intro.p1')}</p>
                        <p>{t('eula.intro.p2')}</p>
                        <p className="font-semibold">{t('eula.intro.p3')}</p>
                        <p className="text-sm bg-yellow-50 border border-yellow-200 p-4 rounded-lg">{t('eula.intro.p4')}</p>
                    </section>

                    {/* 1. Object */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.object.title}</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>{s.object.intro}</p>
                            <ul className="list-disc pl-6 space-y-2">
                                {s.object.items.map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                            <p>{s.object.p1}</p>
                            <p className="bg-red-50 border border-red-200 p-4 rounded-lg font-medium">{s.object.disclaimer}</p>
                        </div>
                    </section>

                    {/* 2. License */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.license.title}</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>{s.license.intro}</p>
                            <ul className="list-disc pl-6 space-y-2">
                                {s.license.items.map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                            <p className="font-medium">{s.license.noOwnership}</p>
                            <p className="font-semibold mt-4">{s.license.prohibitedTitle}</p>
                            <ul className="list-disc pl-6 space-y-2">
                                {s.license.prohibited.map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                        </div>
                    </section>

                    {/* 3. Account */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.account.title}</h2>
                        <div className="space-y-6 text-gray-700">
                            <p>{s.account.intro}</p>

                            <div className="bg-gray-50 p-6 rounded-lg">
                                <h3 className="font-semibold text-lg mb-3">{s.account.guest.title}</h3>
                                <ul className="list-disc pl-6 space-y-2">
                                    {s.account.guest.items.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </div>

                            <div className="bg-blue-50 p-6 rounded-lg">
                                <h3 className="font-semibold text-lg mb-3">{s.account.registered.title}</h3>
                                <p className="mb-3">{s.account.registered.intro}</p>
                                <p className="mb-2">{s.account.registered.benefits}</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    {s.account.registered.benefitItems.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </div>

                            <p className="font-semibold">{s.account.commitments}</p>
                            <ul className="list-disc pl-6 space-y-2">
                                {s.account.commitmentItems.map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                            <p>{s.account.suspension}</p>
                        </div>
                    </section>

                    {/* 4. Plans */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.plans.title}</h2>
                        <div className="space-y-6">
                            <div className="border border-gray-200 rounded-lg p-6">
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">{s.plans.free.title}</h3>
                                <p className="text-gray-700 mb-3">{s.plans.free.intro}</p>
                                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                                    {s.plans.free.items.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                                <p className="text-sm text-gray-500 mt-4">{s.plans.free.note}</p>
                            </div>

                            <div className="border border-blue-200 rounded-lg p-6 bg-blue-50">
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">{s.plans.premium.title}</h3>
                                <p className="text-gray-700 mb-3">{s.plans.premium.intro}</p>
                                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                                    {s.plans.premium.items.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                                <p className="text-sm text-gray-500 mt-4">{s.plans.premium.note}</p>
                            </div>

                            <div className="border border-green-200 rounded-lg p-6 bg-green-50">
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">{s.plans.lifetime.title}</h3>
                                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                                    {s.plans.lifetime.items.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* 5. Subscriptions */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.subscriptions.title}</h2>
                        <div className="space-y-6">
                            <div className="bg-gray-50 p-6 rounded-lg">
                                <h3 className="font-semibold text-lg mb-3">{s.subscriptions.subscription.title}</h3>
                                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                                    {s.subscriptions.subscription.items.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </div>
                            <div className="bg-yellow-50 p-6 rounded-lg">
                                <h3 className="font-semibold text-lg mb-3">{s.subscriptions.trial.title}</h3>
                                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                                    {s.subscriptions.trial.items.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </div>
                            <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                                <h3 className="font-semibold text-lg mb-3">{s.subscriptions.familySharing.title}</h3>
                                <p className="text-gray-700">{s.subscriptions.familySharing.content}</p>
                            </div>
                        </div>
                    </section>

                    {/* 6. Usage */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.usage.title}</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>{s.usage.intro}</p>
                            <p className="font-semibold">{s.usage.prohibitedTitle}</p>
                            <ul className="list-disc pl-6 space-y-2">
                                {s.usage.prohibited.map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                        </div>
                    </section>

                    {/* 7. User Data */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.userData.title}</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>{s.userData.p1}</p>
                            <p className="font-semibold">{s.userData.noGuarantee}</p>
                            <ul className="list-disc pl-6 space-y-2">
                                {s.userData.guaranteeItems.map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                            <p className="bg-red-50 border border-red-200 p-4 rounded-lg">{s.userData.disclaimer}</p>
                        </div>
                    </section>

                    {/* 8. Third Party */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.thirdParty.title}</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>{s.thirdParty.intro}</p>
                            <ul className="list-disc pl-6 space-y-2">
                                {s.thirdParty.items.map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                            <p className="font-semibold mt-4">{s.thirdParty.aiTitle}</p>
                            <ul className="list-disc pl-6 space-y-2">
                                {s.thirdParty.aiItems.map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                            <p>{s.thirdParty.disclaimer}</p>
                        </div>
                    </section>

                    {/* 9. Intellectual Property */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.intellectualProperty.title}</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>{s.intellectualProperty.p1}</p>
                            <p>{s.intellectualProperty.p2}</p>
                        </div>
                    </section>

                    {/* 10. Warranty */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.warranty.title}</h2>
                        <div className="space-y-4 text-gray-700">
                            <p className="font-semibold">{s.warranty.asIs}</p>
                            <p>{s.warranty.intro}</p>
                            <ul className="list-disc pl-6 space-y-2">
                                {s.warranty.items.map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                            <p className="text-sm bg-gray-50 p-4 rounded-lg">{s.warranty.limitation}</p>
                        </div>
                    </section>

                    {/* 11. Privacy */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.privacy.title}</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>
                                {s.privacy.content}
                                <a href="/privacy-policy" className="text-blue-600 hover:text-blue-800 ml-1">{s.privacy.link}</a>
                            </p>
                            <p>{s.privacy.note}</p>
                        </div>
                    </section>

                    {/* 12. Modifications */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.modifications.title}</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>{s.modifications.p1}</p>
                            <p>{s.modifications.p2}</p>
                            <p className="font-medium">{s.modifications.p3}</p>
                        </div>
                    </section>

                    {/* 13. Duration */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.duration.title}</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>{s.duration.p1}</p>
                            <p>{s.duration.p2}</p>
                            <p>{s.duration.p3}</p>
                        </div>
                    </section>

                    {/* 14. Age */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.age.title}</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>{s.age.p1}</p>
                            <p>{s.age.p2}</p>
                        </div>
                    </section>

                    {/* 15. Apple */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.apple.title}</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>{s.apple.p1}</p>
                            <p>{s.apple.p2}</p>
                        </div>
                    </section>

                    {/* 16. Law */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.law.title}</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>{s.law.p1}</p>
                            <p>{s.law.p2}</p>
                        </div>
                    </section>

                    {/* 17. Contact */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.contact.title}</h2>
                        <div className="bg-blue-50 p-6 rounded-lg">
                            <p className="text-lg font-semibold text-gray-900 mb-4">{s.contact.company}</p>
                            <div className="space-y-2 text-gray-700">
                                <p>
                                    <strong>{s.contact.email}:</strong>
                                    <a href="mailto:support@farmway.dev" className="text-blue-600 hover:text-blue-800 ml-1">
                                        support@farmway.dev
                                    </a>
                                </p>
                                <p>
                                    <strong>{s.contact.website}:</strong>
                                    <a href="https://farm.farmway.dev" className="text-blue-600 hover:text-blue-800 ml-1" target="_blank" rel="noopener noreferrer">
                                        https://farm.farmway.dev
                                    </a>
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Effective Date */}
                    <section className="border-t pt-6">
                        <p className="text-sm text-gray-500">
                            {t('eula.lastUpdated').replace('{date}', lastUpdatedDate)}
                        </p>
                    </section>

                </div>

                {/* Back Button */}
                <div className="mt-8 text-center">
                    <button
                        onClick={() => window.history.back()}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
                    >
                        {t('eula.backButton')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EULA;
